import "babel-polyfill";
import React, { Suspense, lazy, useContext, useEffect, useRef } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { Route, Routes } from "react-router-dom";
import { SignIn } from "./pages/SignIn/SignInPage";
import { ResetPassword } from "./pages/ResetPassword/ResetPasswordPage";
import { Welcome } from "./pages/Welcome/WelcomePage";
import { VerifyEmail } from "./pages/VerifyEmail/VerifyEmail";
const SchemaModalContainer = lazy(() => import("./components/SchemaModal/SchemaModal").then((module) => ({ default: module.SchemaModalContainer })));
import { NavBar } from "./components/NavBar/NavBar";
import { NstNavBar } from "./components/NavBar/NstNavBar";
import AppContext from "./context/AppContext";
import Timer from './components/Timer';
import { ToastContainer } from "react-toastify";
import { IConstants } from "./types";
import { isMobile } from "react-device-detect";
import { GenericDassQuery } from "./services/BasicDassQueries";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule } from "ag-grid-community";

const AuthenticatedAppRoutes = lazy(() => import("./components/App/AuthenticatedAppRoutes"));

// Register all Community features 
ModuleRegistry.registerModules([AllCommunityModule]); 

declare const constants: IConstants;





const App = () => {
    const appContextObj = useContext(AppContext);
    const location = useLocation();
    const navigate = useNavigate();
    const isNst = (String(location.pathname).indexOf('/nst') >= 0);
    const user = appContextObj?.user || null; 
    const mar_imp = (appContextObj.isSignedIn(user) && appContextObj.isForwardedSignIn() && appContextObj.navBarState.forwardedSignIn )?  isMobile ? 'mar-120-mobile' : 'mar-120' : ''
    const effectCalled = useRef(false);     // In preparations for React 18

    let navWrapperClass = (location.pathname === '/signin') ? 'besides-data-table' : `${mar_imp} nav-wrapper besides-data-table` ;
    let wrapperClass = "wrapper";

    if (location.pathname === '/' || location.pathname === '/app' || location.pathname === '/nst') {

        wrapperClass = 'welcome';

    } else if (location.pathname === '/signin' || location.pathname === '/nst/network_map' || location.pathname === '/app/network_map') {

        wrapperClass = "";

    }

    useEffect(() => {
        async function checkWhoami() {

            try {
                // Handle remote login
                const searchObj: any = {};
                const hashObj: any = {};
                if (window.location.search) {
                    for (const arg of window.location.search.slice(1).split("&")) {
                        const i = arg.indexOf("=");
                        if (i > 0) {
                            searchObj[arg.slice(0, i)] = decodeURIComponent(arg.slice(i + 1));
                        }
                    }
                }
                if (window.location.hash) {
                    for (const arg of window.location.hash.slice(1).split("&")) {
                        const i = arg.indexOf("=");
                        if (i > 0) {
                            hashObj[arg.slice(0, i)] = decodeURIComponent(arg.slice(i + 1));
                        }
                    }
                }

                if (searchObj.code || hashObj.code) {

                    const tz = new Date().getTimezoneOffset();
                    const tf = new Date().toLocaleTimeString().match(/am|pm/i) ? "ampm" : "24h";
                    const { code, state } = searchObj.code ? searchObj : hashObj;
    
                    await GenericDassQuery("/signin?tz=" + tz + "&tf=" + tf, {
                        data: { code, state },
                        prefix: "", method: "PUT",
                    });
                    
                    // Remove the arguments from the URL to avoid seeing the code
                    history.replaceState(null, null as any, searchObj.code ? window.location.pathname : " ");

                    const user = await appContextObj.loadUser();
                    
                    // After login we reload the whole page
                    navigate(user.ui_settings?.landing_page === "nst"
                        && Object.keys(user).filter(k => k.startsWith("omc_")).length > 0
                            ? "/nst/network_map"
                            : (constants.landing_page_after_signin || `/`)
                    );

                    return;
                }

            } catch (error) {
                console.log("ERROR", error);        // This seems not quite correct.
                appContextObj.signOut();
            }

            // Finally we load the user
            appContextObj.loadUser();
        }

        // React 18 will mount, unmount then mount again. Therefore this will be called twice. To avoid that we
        // need to check if it has already been called.
        if (!effectCalled.current) {
            effectCalled.current = true;
            checkWhoami();
            appContextObj.CheckSystemError();
        }
    }, []);


    // Monitor the status of login. If we get logged out, we navigate back to main page.
    useEffect(() => {
        if (appContextObj.user && !appContextObj.user.userid) {
            console.log("Not signed in");
            if (!["/", "/app/signin", "/app/reset_password", "/app/verify_email"].includes(location.pathname)) {
                console.log("On page that required signin - move to /", location.pathname);
                navigate("/");
            }
        }
    }, [appContextObj.user])

   

    const isAuthenticated = Boolean(appContextObj.user?.userid);
    let rootClassName = isNst ? "ow-nst-wrapper" : "ob-wrapper";
    
    return (
        <div className={rootClassName} 
            onDrop={(event) => { event.preventDefault(); return false; }}
            onDragOver={(event) => { event.preventDefault(); return false; }}
        >

            <Suspense fallback={null}>
                <SchemaModalContainer />
            </Suspense>
            <ToastContainer stacked />
            <div className={`${navWrapperClass}`} >
                <Suspense fallback={null}>
                    {isNst ? <NstNavBar /> : <NavBar />}
                </Suspense>
                <Timer />
            </div>
            <div className={`${wrapperClass}`} id={`id-${wrapperClass}`}>

                {isAuthenticated ? (
                    <Suspense fallback={<div><i className="fa-regular fa-spinner fa-spin"></i></div>}>
                        {privateAppRoutes(appContextObj, navigate, location)}
                    </Suspense>
                ) : (
                    <Suspense fallback={<div><i className="fa-regular fa-spinner fa-spin"></i></div>}>
                        {publicAppRoutes(location)}
                    </Suspense>
                )}

            </div>
        </div>
    );
}





function publicAppRoutes(location: any) {
    return (
        <Routes location={location}>
            <Route path="/" element={<Welcome />}></Route>
            <Route path="/app" element={<Welcome />}></Route>
            <Route path="/signin" element={<SignIn />}></Route>
            <Route path="/app/signin" element={<SignIn />}></Route>
            <Route path="/reset_password" element={<ResetPassword />}></Route>
            <Route path="/app/reset_password" element={<ResetPassword />}></Route>
            <Route path="/verify_email" element={<VerifyEmail />}></Route>
            <Route path="/app/verify_email" element={<VerifyEmail />}></Route>
            <Route path="*" element={<Welcome />}></Route>
        </Routes>
    );
}

function privateAppRoutes(appContextObj: any, navigate: NavigateFunction, location: any) {
    return (
        <Suspense fallback={<div><i className="fa-regular fa-spinner fa-spin"></i></div>}>
            <AuthenticatedAppRoutes appContextObj={appContextObj} navigate={navigate} location={location} />
        </Suspense>
    );
}

export default App;
